import { LitElement, html, css } from 'lit';
import { when } from 'lit/directives/when.js';
import { initMapLayerDock } from './map-layer-dock-init.js';
import '../../../elements/katapult-elements/katapult-button.js';
import '@shoelace-style/shoelace/dist/components/card/card.js';
import '@shoelace-style/shoelace/dist/components/radio-group/radio-group.js';
import '@shoelace-style/shoelace/dist/components/radio/radio.js';
import '@shoelace-style/shoelace/dist/components/spinner/spinner.js';
import { DEFAULT_CESIUM_MAP_BASES, DEFAULT_CESIUM_MAP_BASE_KEY } from '../default-map-bases.js';
import '../map-base-thumbnail/map-base-thumbnail-view.js';
import { OpenPage } from '../../../modules/OpenPage.js';
import { UserCanEditModel } from '../../../modules/UserCanEditModels.js';
/** @import { MapBase } from '../default-map-bases.js' */
/** @import { TemplateResult } from 'lit-element' */
class MapLayerDock extends LitElement {
  static properties = {
    activeMapBaseKey: { type: String },
    userGroup: { type: String },
    userId: { type: String },
    _isOpen: { type: Boolean, state: true },
    _activeTabKey: { type: String, state: true },
    _isLoading: { type: Boolean, state: true },
    _error: { type: String, state: true },
    _firebaseMapBaseConfigs: { type: Object, state: true },
    _userIsModelEditor: { type: Boolean, state: true }
  };

  static styles = css`
    .main-layers-button {
      align-self: flex-start;
      width: fit-content;
    }
    .dock {
      display: flex;
      flex-direction: row;
      align-items: flex-end;
      box-shadow: var(--sl-shadow-x-large);
      background-color: var(--sl-color-neutral-0);
      border-radius: var(--sl-border-radius-x-large);
      margin-left: var(--sl-spacing-small);
      overflow: hidden;
    }

    .rail {
      display: flex;
      flex-direction: column;
      gap: var(--sl-spacing-small);
      overflow: hidden;
      height: -webkit-fill-available;
    }

    #railItems {
      height: -webkit-fill-available;
    }

    .dock-item {
      display: flex;
      flex-direction: row;
      gap: var(--sl-spacing-small);
      cursor: pointer;
      align-items: center;
      padding: var(--sl-spacing-small);
      color: var(--sl-color-neutral-800);
    }

    .dock-item[active] {
      color: var(--sl-color-neutral-0);
      background-color: var(--secondary-color);
    }

    .content-panel {
      width: 300px;
      min-height: 400px;
      max-height: 600px;
      box-shadow: var(--sl-shadow-x-large);
      display: block;
    }

    .content-panel[hidden] {
      display: none;
    }

    .content-panel::part(body) {
      padding: 0;
      max-height: 600px;
      display: flex;
      flex-direction: column;
    }

    .scroll-area {
      flex: 1;
      overflow-y: auto;
      min-height: 0;
    }

    .footer {
      background-color: var(--sl-color-neutral-0);
      border-top: 1px solid var(--sl-color-neutral-200);
      padding: var(--sl-spacing-small);
    }

    .content-section-label {
      color: var(--sl-color-neutral-900);
      font-size: var(--sl-font-size-medium);
      font-weight: var(--sl-font-weight-semibold);
      margin: var(--sl-spacing-small);
    }

    .map-base-list {
      display: flex;
      flex-direction: column;
      gap: var(--sl-spacing-2x-small);
    }

    #mapBaseItems sl-radio {
      display: block;
    }

    #mapBaseItems sl-radio:hover {
      background: var(--sl-color-neutral-100);
    }

    #mapBaseItems sl-radio::part(base) {
      width: -webkit-fill-available;
      padding: var(--sl-spacing-small);
      padding-right: var(--sl-spacing-large);
    }

    #mapBaseItems sl-radio::part(label) {
      flex: 1;
    }

    #mapBaseItems sl-radio::part(control) {
      order: 1;
      margin: auto;
    }

    .map-base-row {
      display: flex;
      flex-direction: row;
      align-items: center;
      gap: var(--sl-spacing-small);
    }

    .map-base-name {
      font-size: var(--sl-font-size-small);
      color: var(--sl-color-neutral-800);
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .error-message {
      color: var(--sl-color-danger-600);
      font-size: var(--sl-font-size-small);
      padding: var(--sl-spacing-small);
    }
  `;

  #store;
  #controller;
  #unsubscribe;
  #clickOutsideHandler;

  constructor() {
    super();

    // Initialize the store and controller from the dock module
    const { store, controller } = initMapLayerDock();
    this.#store = store;
    this.#controller = controller;

    // Set default values for all reactive state properties
    this.activeMapBaseKey = DEFAULT_CESIUM_MAP_BASE_KEY;
    this.userGroup = null;
    this.userId = null;
    this._isOpen = false;
    this._activeTabKey = null;
    this._isLoading = false;
    this._error = null;
    this._firebaseMapBaseConfigs = null;
    this._userIsModelEditor = false;

    // Handler that closes the menu when clicking outside the dock
    this.#clickOutsideHandler = (event) => {
      const isClickOutsideDock = !event.composedPath().includes(this);
      if (this._isOpen && isClickOutsideDock) {
        this.#store.getState().setIsOpen(false);
      }
    };
  }

  connectedCallback() {
    super.connectedCallback();

    // Subscribe to the store and sync all state properties for reactive rendering
    this.#unsubscribe = this.#store.subscribe((state) => {
      this._isOpen = state.isOpen;
      this._activeTabKey = state.activeTabKey;
      this.activeMapBaseKey = state.activeMapBaseKey;
      this._isLoading = state.isLoading;
      this._error = state.error;

      const firebaseMapBaseConfigs = state.firebaseMapBaseConfigs ?? {};
      Object.entries(firebaseMapBaseConfigs).forEach(([key, config]) => (config.key = key));
      this._firebaseMapBaseConfigs = firebaseMapBaseConfigs;
    });

    // Listen for clicks outside to auto close the dock
    document.addEventListener('click', this.#clickOutsideHandler, true);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#unsubscribe?.();
    this.#controller.disconnectMapLayerDock();
    document.removeEventListener('click', this.#clickOutsideHandler, true);
  }

  willUpdate(changedProperties) {
    // Sync the incoming mapBase property into the store
    if (changedProperties.has('activeMapBaseKey') && this.activeMapBaseKey != null) this.#dispatchMapBaseChangedEvent();

    // Set up the map bases listener when the user group changes
    if (changedProperties.has('userGroup')) this.#controller.setUpMapBasesListener(this.userGroup);

    // Refresh the user's model editor access whenever the active user or company changes
    if (changedProperties.has('userGroup') || changedProperties.has('userId')) this.#refreshUserCanEditModelEditor();
  }

  render() {
    return html`
      <div
        class="dock"
        style=${this._isOpen ? 'border-radius: var(--sl-border-radius-x-large)' : 'border-radius: var(--sl-border-radius-circle)'}
      >
        <div class="rail">
          ${when(
            this._isOpen,
            () => html`
              <div id="railItems">
                <div class="dock-item" ?active=${this._activeTabKey === 'mapBase'} @click=${() => this.#controller.selectTab('mapBase')}>
                  <katapult-icon icon="map"></katapult-icon>
                  <span>Map Base</span>
                </div>
              </div>
            `
          )}
          <katapult-button
            class="main-layers-button"
            iconOnly
            noBorder
            icon="layers"
            .callback=${() => this.#toggleDockVisibility()}
          ></katapult-button>
        </div>
        <sl-card class="content-panel" ?hidden=${!this._isOpen || this._activeTabKey == null}> ${this.#renderActiveTabContent()} </sl-card>
      </div>
    `;
  }

  /**
   * Renders the content panel body based on which tab is active.
   * @returns {TemplateResult|null} A Lit template for the active tab, or null when no tab is selected.
   */
  #renderActiveTabContent() {
    if (this._activeTabKey === 'mapBase') return this.#renderMapBaseTab();
    return null;
  }

  /**
   * Renders the Map Base tab content, including the scrollable list and footer action.
   * @returns {TemplateResult} A Lit template for the map base tab.
   */
  #renderMapBaseTab() {
    return html`
      <div class="scroll-area">${this.#renderMapBaseList()}</div>
      ${when(
        this._userIsModelEditor,
        () => html`
          <div class="footer">
            <katapult-button icon="settings" .callback=${() => this.#openMapBaseEditor()} style="width: -webkit-fill-available;"
              >Manage Map Bases</katapult-button
            >
          </div>
        `
      )}
    `;
  }

  /**
   * Renders the grouped radio list of default and imported map bases.
   * @returns {TemplateResult} - A Lit template rendering the map base selection list
   * */
  #renderMapBaseList() {
    // If there's an error, show the error message instead of the list
    if (this._error) return html`<div class="error-message">${this._error}</div>`;

    const defaultMapBaseConfigs = Object.values(DEFAULT_CESIUM_MAP_BASES);
    const firebaseMapBaseConfigs = Object.values(this._firebaseMapBaseConfigs ?? {});

    return html`
      <sl-radio-group id="mapBaseItems" value=${this.activeMapBaseKey} @sl-change=${this.#mapBaseSelected}>
        <div class="map-base-list">
          <span class="content-section-label">Default</span>
          ${defaultMapBaseConfigs.map((mapBase) => this.#renderMapBaseRow(mapBase))}
          ${when(
            firebaseMapBaseConfigs.length != 0,
            () => html`
              <span class="content-section-label">Imported</span>
              ${firebaseMapBaseConfigs.map((mapBase) => this.#renderMapBaseRow(mapBase))}
            `
          )}
        </div>
      </sl-radio-group>
    `;
  }

  /**
   * Renders a single radio row for a map base option.
   * @param {MapBase} mapBase
   * @return {TemplateResult} A Lit template rendering a radio option for the given map base, including its thumbnail and name.
   */
  #renderMapBaseRow(mapBase) {
    const { key, name } = mapBase;
    const isActiveMapBase = this.activeMapBaseKey === key;
    return html`
      <sl-radio value=${key} style="color: ${isActiveMapBase ? 'var(--secondary-color)' : 'var(--sl-color-neutral-800)'};">
        <div class="map-base-row">
          ${this.#renderMapBaseThumbnail(mapBase)}
          <span class="map-base-name">${name}</span>
        </div>
      </sl-radio>
    `;
  }

  /**
   * Renders a thumbnail for the given map base
   * @param {MapBase} mapBase - The map base configuration object containing details like type and URL.
   * @returns {TemplateResult} - A Lit template rendering the appropriate thumbnail based on the map base type.
   */
  #renderMapBaseThumbnail(mapBase) {
    return html`<map-base-thumbnail .mapBase=${mapBase} .width=${48} .height=${34}></map-base-thumbnail>`;
  }

  /**
   * Handles the radio group change event and delegates the selection to the controller.
   * @param {CustomEvent} event
   */
  #mapBaseSelected(event) {
    this.#controller.selectMapBase(event.target.value);
  }

  /** Determines whether the active user has model editor access for their active company and updates the reactive state. */
  async #refreshUserCanEditModelEditor() {
    if (!this.userGroup || !this.userId) {
      this._userIsModelEditor = false;
      return;
    }

    this._userIsModelEditor = await UserCanEditModel(this.userId, this.userGroup);
  }

  /** Opens the model editor's map base editor in a new tab for the active company. */
  #openMapBaseEditor() {
    OpenPage('model-editor/', { hash: `${this.userGroup}/map_bases`, target: '_blank' });
  }

  /** Toggles the dock between its open and closed states. */
  #toggleDockVisibility() {
    const state = this.#store.getState();
    this.#store.getState().setIsOpen(!state.isOpen);
  }

  /** Dispatches a 'map-base-changed' custom event with the active map base config. */
  #dispatchMapBaseChangedEvent() {
    const firebaseMapBaseConfigs = this.#store.getState().firebaseMapBaseConfigs ?? {};
    const mapBaseConfig = firebaseMapBaseConfigs[this.activeMapBaseKey] ?? DEFAULT_CESIUM_MAP_BASES[this.activeMapBaseKey];
    const mapBaseChangedEvent = new CustomEvent('map-base-changed', {
      bubbles: true,
      composed: true,
      detail: { value: { ...mapBaseConfig, key: this.activeMapBaseKey } }
    });
    this.dispatchEvent(mapBaseChangedEvent);
  }
}

customElements.define('map-layer-dock', MapLayerDock);
